import { type ReactNode, useState } from 'react';
import { Card, SegmentedTabs } from '@skupreme/shared/ui';
import type {
  ManualShipItem,
  ManualShipPayload,
  ManualShipProviderOption,
} from '../../lib/manual-ship';
import type { LegacyOption } from '../fulfillment/fields';
import { MultipleShipmentForm } from './MultipleShipmentForm';
import { SingleShipmentForm } from './SingleShipmentForm';

export type ManualShipCardProps = {
  /** Every line on the order: what a single shipment covers. */
  items: ManualShipItem[];
  /** What the selected warehouse can ship: one row each in multiple mode. */
  orderItems: ManualShipItem[];
  providerOptions: ManualShipProviderOption[];
  providersLoading?: boolean;
  carriers: LegacyOption[];
  carriersLoading?: boolean;
  defaultProviderId?: string;
  isSaving?: boolean;
  onSubmit: (payload: ManualShipPayload) => void;
  onCancel: () => void;
  modeControl?: ReactNode;
};

const MODES = [
  { id: 'single', label: 'Single' },
  { id: 'multiple', label: 'Multiple' },
];

/**
 * Records a shipment bought elsewhere. No label is purchased here: the order is
 * marked shipped against a tracking number the carrier already issued.
 */
export function ManualShipCard({
  items,
  orderItems,
  providerOptions,
  providersLoading,
  carriers,
  carriersLoading,
  defaultProviderId,
  isSaving,
  onSubmit,
  onCancel,
  modeControl,
}: ManualShipCardProps) {
  const [mode, setMode] = useState('single');
  const canSplit = orderItems.length > 1;

  const formProps = {
    providerOptions,
    providersLoading,
    carriers,
    carriersLoading,
    defaultProviderId,
    isSaving,
    onSubmit,
    onCancel,
  };

  return (
    <Card
      title="Ship manually"
      action={
        canSplit || modeControl ? (
          <span className="sk:flex sk:items-center sk:gap-3">
            {canSplit ? (
              <SegmentedTabs
                items={MODES}
                selectedKey={mode}
                onSelectionChange={setMode}
                label="Shipments per order"
              />
            ) : null}
            {modeControl}
          </span>
        ) : undefined
      }
      bodyClassName="sk:flex sk:flex-col sk:gap-4"
    >
      <span className="sk:text-xs sk:text-fg-tertiary">
        Add a tracking number for a shipment you bought outside Skupreme.
      </span>

      {canSplit && mode === 'multiple' ? (
        <MultipleShipmentForm items={orderItems} {...formProps} />
      ) : (
        <SingleShipmentForm items={items} {...formProps} />
      )}
    </Card>
  );
}
